import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

const BriefsContainer = ({ jobData, clientData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [clientBriefs, setClientBriefs] = useState(clientData?.briefUrls || []);
  const [jobBriefs, setJobBriefs] = useState(jobData?.briefUrls || []);

  // Update state when props change
  useEffect(() => {
    setClientBriefs(clientData?.briefUrls || []);
    setJobBriefs(jobData?.briefUrls || []);
  }, [jobData, clientData]);

  const handleAddBrief = async (isClientBrief) => {
    if (!isAdmin) return;

    try {
      if (isClientBrief) {
        const newClientBriefs = [...clientBriefs, ""];
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          briefUrls: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs, ""];
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          briefUrls: newJobBriefs,
        });
      }
    } catch (error) {
      console.error("Error adding brief:", error);
    }
  };

  const handleDeleteBrief = async (index, isClientBrief) => {
    if (!isAdmin) return;

    try {
      if (isClientBrief) {
        const newClientBriefs = [...clientBriefs];
        newClientBriefs.splice(index, 1);
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          briefUrls: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs];
        newJobBriefs.splice(index, 1);
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          briefUrls: newJobBriefs,
        });
      }
    } catch (error) {
      console.error("Error deleting brief:", error);
    }
  };

  const handleOpenBrief = (url) => {
    if (!url) return;

    // Add https:// if not present
    let formattedUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      formattedUrl = `https://${url}`;
    }

    window.open(formattedUrl, "_blank");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const renderBriefSection = (briefs, label, isClientBrief) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color:
                theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            }}
          >
            {label}
          </Typography>

          {isAdmin && (
            <IconButton
              size="small"
              onClick={() => handleAddBrief(isClientBrief)}
              sx={{
                ml: 1,
                bgcolor:
                  theme === "dark"
                    ? "rgba(60,60,60,0.8)"
                    : "rgba(240,240,240,0.8)",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.8)"
                    : "rgba(0,0,0,0.7)",
                boxShadow:
                  theme === "dark"
                    ? "0 1px 3px rgba(0,0,0,0.3)"
                    : "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor:
                    theme === "dark"
                      ? "rgba(70,70,70,0.9)"
                      : "rgba(245,245,245,0.9)",
                },
              }}
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {briefs &&
            briefs?.map((brief, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{ marginBottom: "8px" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <UniversalTextField
                    value={brief}
                    hiddenLabel
                    disabled={!isAdmin}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                    onCallback={(newValue) => {
                      if (isClientBrief) {
                        const newClientBriefs = [...clientBriefs];
                        newClientBriefs[index] = newValue;
                        setClientBriefs(newClientBriefs);
                        updateDoc(
                          doc(db, "ClientDatabase", clientData.id),
                          { briefUrls: newClientBriefs }
                        ).catch((e) => console.error(e));
                      } else {
                        const newJobBriefs = [...jobBriefs];
                        newJobBriefs[index] = newValue;
                        setJobBriefs(newJobBriefs);
                        updateDoc(
                          doc(db, "JobData", jobData.id),
                          { briefUrls: newJobBriefs }
                        ).catch((e) => console.error(e));
                      }
                      return newValue;
                    }}
                    sx={{ marginTop: 0 }}
                  />
                  {brief && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenBrief(brief)}
                      sx={{
                        bgcolor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.8)"
                            : "rgba(240,240,240,0.8)",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.8)"
                            : "rgba(0,0,0,0.7)",
                        boxShadow:
                          theme === "dark"
                            ? "0 1px 3px rgba(0,0,0,0.3)"
                            : "0 1px 3px rgba(0,0,0,0.1)",
                        "&:hover": {
                          bgcolor:
                            theme === "dark"
                              ? "rgba(70,70,70,0.9)"
                              : "rgba(245,245,245,0.9)",
                        },
                      }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteBrief(index, isClientBrief)}
                    disabled={!isAdmin}
                    sx={{
                      bgcolor:
                        theme === "dark"
                          ? "rgba(60,60,60,0.8)"
                          : "rgba(240,240,240,0.8)",
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.8)"
                          : "rgba(0,0,0,0.7)",
                      boxShadow:
                        theme === "dark"
                          ? "0 1px 3px rgba(0,0,0,0.3)"
                          : "0 1px 3px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor:
                          theme === "dark"
                            ? "rgba(70,70,70,0.9)"
                            : "rgba(245,245,245,0.9)",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </motion.div>
            ))}
        </motion.div>
      </Box>
    );
  };

  // Defensive check to prevent component from disappearing
  if (!jobData || !clientData) {
    return (
      <FormControl
        fullWidth
        sx={{
          marginTop: 2,
          position: "relative",
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
        variant="filled"
      >
        <Box sx={{ padding: 2, opacity: 0.5 }}>Loading briefs...</Box>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
      variant="filled"
    >
      {renderBriefSection(clientBriefs, "Client Briefs", true)}
      {renderBriefSection(jobBriefs, "Job Briefs", false)}
    </FormControl>
  );
};

export default BriefsContainer;
